import React, { useState } from 'react';
import { GitBranch, Plus, Trash2, ToggleLeft, ToggleRight, FlaskConical, ChevronDown, ChevronUp } from 'lucide-react';

const AutomationRules = ({ rules, onToggle, onDelete, onAdd }) => {
  const [expanded, setExpanded] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [newRule, setNewRule] = useState({ name: '', trigger: '', condition: '', action: '', abTest: false });

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev?.[id] }));

  const handleAdd = () => {
    if (newRule?.name && newRule?.trigger) {
      onAdd?.(newRule);
      setNewRule({ name: '', trigger: '', condition: '', action: '', abTest: false });
      setShowForm(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-violet-600" />
          <h2 className="text-base font-semibold text-gray-900">Advanced Automation Rules</h2>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Rule
        </button>
      </div>
      {showForm && (
        <div className="px-6 py-4 bg-violet-50 border-b border-violet-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input
              type="text"
              placeholder="Rule name"
              value={newRule?.name}
              onChange={e => setNewRule(p => ({ ...p, name: e?.target?.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <input
              type="text"
              placeholder="Trigger event (e.g. lead.score > 80)"
              value={newRule?.trigger}
              onChange={e => setNewRule(p => ({ ...p, trigger: e?.target?.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <input
              type="text"
              placeholder="Condition (e.g. industry == 'SaaS')"
              value={newRule?.condition}
              onChange={e => setNewRule(p => ({ ...p, condition: e?.target?.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <input
              type="text"
              placeholder="Action (e.g. send_email + create_ghl_contact)"
              value={newRule?.action}
              onChange={e => setNewRule(p => ({ ...p, action: e?.target?.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div className="flex items-center gap-3 mb-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={newRule?.abTest}
                onChange={e => setNewRule(p => ({ ...p, abTest: e?.target?.checked }))}
                className="rounded border-gray-300 text-violet-600"
              />
              Enable A/B Testing
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors">Save Rule</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-white border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">Cancel</button>
          </div>
        </div>
      )}
      <div className="divide-y divide-gray-50">
        {rules?.map((rule) => (
          <div key={rule?.id} className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${rule?.active ? 'bg-violet-500' : 'bg-gray-300'}`} />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{rule?.name}</span>
                    {rule?.abTest && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                        <FlaskConical className="w-3 h-3" /> A/B
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{rule?.trigger}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400 hidden md:block">{rule?.executions} runs</span>
                <button onClick={() => onToggle?.(rule?.id)} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
                  {rule?.active ? <ToggleRight className="w-5 h-5 text-violet-500" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                </button>
                <button onClick={() => toggleExpand(rule?.id)} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors">
                  {expanded?.[rule?.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <button onClick={() => onDelete?.(rule?.id)} className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {expanded?.[rule?.id] && (
              <div className="mt-3 pl-5 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 mb-1">Condition</p>
                  <p className="text-sm text-gray-700 font-mono">{rule?.condition}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 mb-1">Action</p>
                  <p className="text-sm text-gray-700 font-mono">{rule?.action}</p>
                </div>
                {rule?.abTest && (
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs font-medium text-amber-600 mb-1">A/B Test Results</p>
                    <p className="text-sm text-gray-700">A: {rule?.abResults?.a}% · B: {rule?.abResults?.b}%</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AutomationRules;
